import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { callLogin, callRegister, callKakaoLogin, callNaverLogin, callAppleLogin, callGetMyRoles, callSetActiveType, callGetMe } from "api/MuyeonApi";
import { kakaoRedirectUri } from "config/kakao";
import { getDeviceInfoSvc, setDeviceInfoSvc } from "common/deviceService";
import { syncActiveTypeNative } from "common/nativeBridge";
import { setTokenSvc } from "common/tokenService";
import { heldTypes } from "constants/permissions";
import { ROLE_LABELS } from "common/activeTypeGate";
import { showToast } from "util/appToast";
import { clearGuestDrafts, clearUserDrafts, consumePreserveDraftOnLogin } from "util/guestDraft";
import { clearJobBoardTabMemory } from "util/jobBoardMemory";
import { LOGIN_PROVIDER, setLastLoginProvider } from "common/lastLoginProvider";
import { SPACE_FEATURE_ENABLED } from "constants/featureFlags";
import { getAppSocket, disconnectAppSocket } from "common/appSocket";
import { hasNativeBridge } from "util/userAgent";

// 활성유형(활동 컨텍스트) — 클라 저장. 보유유형 중 하나 또는 "GENERAL"(고객 모드).
//  권한 판정(백엔드)·알림은 보유유형 합집합 유지, 활성유형은 화면(탭/홈/MY) UX 렌즈일 뿐.
export const GENERAL_TYPE = "GENERAL";
const ACTIVE_KEY = "muyeon.activeType";

// 로그인/가입 직후 네이티브 동기화:
//  1) JWT 를 네이티브 키체인에 저장 → 네이티브 채팅(ChatService/소켓)이 Bearer 인증 가능
//  2) FCM 디바이스 토큰을 서버(device_tokens)에 등록(userId 연결)
async function syncNativeAfterAuth(data) {
  try {
    if (data?.accessToken) await setTokenSvc({ accessToken: data.accessToken, refreshToken: data.accessToken });
  } catch {}
  try {
    const d = await getDeviceInfoSvc(); // 네이티브 앱일 때만 값 있음(deviceToken=fcm_token)
    if (d) await setDeviceInfoSvc(d);
  } catch {}
}

/**
 * 무용연 인증 컨텍스트 (이메일+비번 JWT).
 *  - 토큰/유저를 localStorage 'muyeonAuth' 에 저장
 *  - 열람은 공개, 행동·MY 는 로그인 필요 (RequireAuth / useRequireAuth)
 */
const AuthContext = createContext(null);
const STORAGE = "muyeonAuth";

const read = () => {
  try {
    const s = localStorage.getItem(STORAGE);
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
};

const readActive = () => {
  try { return localStorage.getItem(ACTIVE_KEY) || null; } catch { return null; }
};

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(read); // { accessToken, user } | null
  const [activeType, setActiveTypeState] = useState(readActive);
  // 역할 추가 직후 같은 동작 안에서 유형을 전환할 때도 최신 보유 유형을 보도록 한다.
  const authRef = useRef(auth);
  authRef.current = auth;

  // 활성유형 유효성 — 보유 pro유형(∪ GENERAL) 밖이면 기본값으로. HOBBY 는 GENERAL 로 취급(중복 제거).
  const heldKey = heldTypes(auth?.user).join(",");
  useEffect(() => {
    const pro = heldTypes(auth?.user).filter((t) => t !== "HOBBY");
    const valid = [...pro, GENERAL_TYPE];
    if (!activeType || !valid.includes(activeType)) {
      // 서버에 저장된 마지막 활성유형(재설치·기기변경 복원) 우선, 없으면 첫 pro 유형
      const server = auth?.user?.activeType;
      const def = (server && valid.includes(server) ? server : null) || pro[0] || GENERAL_TYPE;
      setActiveTypeState(def);
      try { localStorage.setItem(ACTIVE_KEY, def); } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heldKey]);

  // 전환 중 보호 — 전환마다 번호를 올리고 저장(PATCH) 중인 건수를 센다.
  //  ★ 2026-10-19 포커스 복귀 refreshRoles 의 GET 이 전환 전에 출발해 늦게 도착하면, 방금 바꾼 강사를
  //    서버의 옛 값(일반)으로 덮어 '들어갈 때마다 일반으로 돌아가는' 현상이 났다(Android 는 매번 재현).
  const switchSeq = useRef(0);
  const savingCount = useRef(0);
  const activeRef = useRef(activeType);
  activeRef.current = activeType;

  const applyActiveType = (next) => {
    const prev = activeRef.current;
    const seq = ++switchSeq.current;
    activeRef.current = next;
    setActiveTypeState(next);
    try { localStorage.setItem(ACTIVE_KEY, next); } catch {}
    // 서버에도 저장(복원·푸시 라우팅·다른 기기). 실패하면 화면만 바뀐 채 다음 복귀 때 되돌아가므로 원복하고 알린다.
    if (loggedInRef.current) {
      savingCount.current += 1;
      callSetActiveType(next)
        .catch(() => {
          if (switchSeq.current !== seq || !prev || prev === next) return;
          activeRef.current = prev;
          setActiveTypeState(prev);
          try { localStorage.setItem(ACTIVE_KEY, prev); } catch {}
          syncActiveTypeNative(prev);
          showToast("유형 전환을 저장하지 못했어요. 잠시 후 다시 시도해 주세요.");
        })
        .finally(() => { savingCount.current -= 1; });
    }
    // 네이티브 X-Active-Type 헤더 동기화 + 전환 토스트(앱=네이티브 토스트/브라우저=웹 토스트)
    if (next !== prev) {
      const inApp = syncActiveTypeNative(next, true);
      if (!inApp) showToast(`${ROLE_LABELS[next] || next} 유형으로 전환됐어요`);
    } else {
      syncActiveTypeNative(next);
    }
  };

  // 활동 유형 전환 — 보유 pro유형(∪ GENERAL) 내에서만. HOBBY → GENERAL.
  //  캐시된 보유유형에 없으면(방금 추가·승인되어 아직 캐시 전) 일반으로 떨어뜨리지 않고 서버에서 확인한 뒤 전환한다.
  const setActiveType = (type) => {
    const t = type === "HOBBY" ? GENERAL_TYPE : type;
    const valid = [...heldTypes(authRef.current?.user).filter((x) => x !== "HOBBY"), GENERAL_TYPE];
    if (valid.includes(t)) return applyActiveType(t);
    if (!loggedInRef.current) return;
    // 서버에도 없으면 그대로 둔다(앱에서 '추가→전환'을 같이 보내면 추가 완료 콜백이 다시 전환해 준다).
    refreshRoles().then((d) => {
      if (Array.isArray(d?.held) && d.held.includes(t)) applyActiveType(t);
    });
  };

  // 서버 최신 보유역할로 user.memberTypes 동기화 — 관리자 승인 등 서버측 역할 변경도
  //  재로그인 없이 UI(탭/메뉴 show·hide·권한 가드)에 반영. 값이 같으면 setState 스킵.
  const loggedInRef = useRef(!!auth?.accessToken);
  loggedInRef.current = !!auth?.accessToken;
  const refreshRoles = useCallback(async () => {
    if (!loggedInRef.current) return null;
    const seqAtStart = switchSeq.current;
    try {
      const r = await callGetMyRoles();
      const held = r?.data?.held;
      const serverActiveType = r?.data?.activeType;
      if (!Array.isArray(held)) return null;
      // ★ 2026-10-20 조회 중에 전환이 있었으면(앱 회원유형 화면 포함) 이 응답은 통째로 옛 값이다.
      //  보유 목록만 반영해도 방금 추가한 강사가 빠져 활동유형이 일반으로 되돌아갔다 — 아무것도 반영하지 않는다.
      if (switchSeq.current !== seqAtStart) return r.data;
      const safeActiveType = serverActiveType === GENERAL_TYPE || held.includes(serverActiveType)
        ? serverActiveType
        : GENERAL_TYPE;
      setAuth((prev) => {
        if (!prev?.user) return prev;
        const cur = Array.isArray(prev.user.memberTypes) ? prev.user.memberTypes : [];
        // 집합 동일하면(순서 무관) 변경 없음 → 불필요한 리렌더/스토리지 쓰기 방지.
        if (cur.length === held.length && [...cur].sort().join() === [...held].sort().join()
          && prev.user.activeType === safeActiveType) {
          return prev;
        }
        const next = { ...prev, user: { ...prev.user, memberTypes: held, activeType: safeActiveType } };
        try { localStorage.setItem(STORAGE, JSON.stringify(next)); } catch {}
        return next;
      });
      // 관리자 승인으로 서버의 활동 유형이 바뀌면 웹과 네이티브도 즉시 같은 유형으로 맞춘다.
      //  단, 조회 중에 전환했거나 전환 저장이 아직 진행 중이면 이 응답은 옛 값이라 덮지 않는다.
      if (switchSeq.current === seqAtStart && savingCount.current === 0) {
        activeRef.current = safeActiveType;
        setActiveTypeState(safeActiveType);
        try { localStorage.setItem(ACTIVE_KEY, safeActiveType); } catch {}
        syncActiveTypeNative(safeActiveType);
      }
      return r.data;
    } catch { return null; }
  }, []);

  // 서버 최신 약관 동의 상태로 user.termsAgreed 동기화.
  //  ★ 종전엔 로그인 응답 값만 localStorage 에 두어, 관리자가 약관을 '재동의'로 개정해도 이미 로그인한
  //    회원에게는 동의 화면(AppRouter 게이트)이 뜨지 않았다. 앱 시작·포그라운드 복귀 때 다시 읽는다.
  //  잦은 호출을 막으려고 5분 안에는 다시 부르지 않는다.
  const termsCheckedAt = useRef(0);
  const refreshTerms = useCallback(async () => {
    if (!loggedInRef.current) return;
    if (Date.now() - termsCheckedAt.current < 5 * 60 * 1000) return;
    termsCheckedAt.current = Date.now();
    try {
      const u = (await callGetMe())?.data;
      if (!u || typeof u.termsAgreed !== "boolean") return;
      setAuth((prev) => {
        if (!prev?.user) return prev;
        // 가입 미완료(signupIncomplete)·본인확인(phoneVerified)도 함께 — 로그인 응답에만 의존하면
        //  수정 전에 로그인한 세션(웹·앱 웹뷰 저장소)은 소셜 가입 미완료 게이트를 영영 못 탔다.
        if (prev.user.termsAgreed === u.termsAgreed && !!prev.user.termsReconsent === !!u.termsReconsent
          && !!prev.user.signupIncomplete === !!u.signupIncomplete && !!prev.user.phoneVerified === !!u.phoneVerified) return prev;
        const next = { ...prev, user: { ...prev.user, termsAgreed: u.termsAgreed, termsReconsent: !!u.termsReconsent,
          signupIncomplete: !!u.signupIncomplete, phoneVerified: !!u.phoneVerified } };
        try { localStorage.setItem(STORAGE, JSON.stringify(next)); } catch {}
        return next;
      });
    } catch { termsCheckedAt.current = 0; /* 실패하면 다음 기회에 다시 */ }
  }, []);

  // 앱이 서버에 이미 저장한 전환(회원유형 화면·전환 알럿 등) → 웹은 화면만 맞춘다.
  //  ★ 2026-10-20 종전엔 setActiveType 으로 받아 PATCH 를 한 번 더 보냈고, 앱이 웹의 동기화 값을 되돌려 보낼 때는
  //   늦게 도착한 옛 값(일반)까지 서버에 다시 저장해, 앱에서 강사로 바꾼 뒤 다시 열면 일반으로 보였다.
  //   전환 번호를 올려 진행 중이던 옛 조회도 무시한다.
  const adoptNativeActiveType = (type) => {
    const t = type === "HOBBY" || (!SPACE_FEATURE_ENABLED && type === "SPACE") ? GENERAL_TYPE : type;
    if (!t) return;
    switchSeq.current += 1;
    const valid = [...heldTypes(authRef.current?.user).filter((x) => x !== "HOBBY"), GENERAL_TYPE];
    if (!valid.includes(t)) {
      // 방금 추가된 유형 — 보유 목록부터 새로 읽는다(서버엔 이미 저장돼 있어 활동유형도 함께 맞춰진다).
      refreshRoles();
      return;
    }
    activeRef.current = t;
    setActiveTypeState(t);
    try { localStorage.setItem(ACTIVE_KEY, t); } catch {}
  };

  // 네이티브에서 활성유형 전환(RoleGate) → 웹 상태 동기화 콜백 + 현재값 네이티브 초기 동기.
  useEffect(() => {
    window.__onActiveTypeChanged = adoptNativeActiveType;
    syncActiveTypeNative(activeType || GENERAL_TYPE);
    return () => { delete window.__onActiveTypeChanged; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeType]);

  // 앱 알림 허용 안내(iOS·AOS PushPrimer) 결과 — 허용했으면 기기 정보를 다시 저장해
  //  로그인 때 '알림 꺼짐'으로 저장된 기기(device_tokens.pushEnabled)를 켠다(2026-10-25).
  useEffect(() => {
    window.__onNotificationConsent = async (granted) => {
      if (!granted) return;
      try {
        const d = await getDeviceInfoSvc();
        if (d) await setDeviceInfoSvc(d);
      } catch {}
    };
    return () => { delete window.__onNotificationConsent; };
  }, []);

  // 앱 로드 시 이미 로그인 상태면(localStorage 유지) 네이티브 키체인에 토큰 동기화 + 역할 동기화.
  // → 재로그인 없이도 네이티브 채팅 인증 가능 + FCM 토큰 재등록.
  useEffect(() => {
    if (auth?.accessToken) {
      syncNativeAfterAuth(auth);
      refreshRoles();
      refreshTerms();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lastDeviceSync = useRef(Date.now());
  // 앱 포그라운드 복귀(탭 visible / 창 focus) 시 역할 재동기화 —
  //  세션 유지 중 관리자 승인 등으로 역할이 바뀌어도 앱 재시작 없이 반영.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        refreshRoles(); refreshTerms();
        // 설정에서 알림을 켜고 돌아와도 서버가 알도록 기기·알림 허용 상태를 다시 보낸다(1분에 한 번).
        const a = authRef.current;
        if (a?.accessToken && Date.now() - lastDeviceSync.current > 60000) {
          lastDeviceSync.current = Date.now();
          syncNativeAfterAuth(a);
        }
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [refreshRoles, refreshTerms]);

  // ApiClient(웹) 토큰 갱신 실패 시 dispatch → 강제 로그인 이동 없이 '상태만 로그아웃'.
  //  공개 페이지는 그대로 열람 유지(광고 유입자), 인증 라우트는 PrivateRouter 가 처리.
  useEffect(() => {
    const onForceLogout = () => {
      setAuth(null);
      try { localStorage.removeItem(STORAGE); } catch {}
      // 옛 토큰 소켓을 남기면 다음 로그인 때 '다른 기기 로그인' 신호를 받아 방금 한 로그인을 끊을 수 있다.
      disconnectAppSocket();
    };
    window.addEventListener("muyeonForceLogout", onForceLogout);
    return () => window.removeEventListener("muyeonForceLogout", onForceLogout);
  }, []);

  // ★ 2026-10-30 웹 브라우저는 로그인 중이면 소켓을 바로 연결해 둔다 — 다른 기기 로그인·계정 정지를
  //  그 자리에서 받기 위해(종전엔 채팅·견적 배지 화면에 들어가야 연결됐다). 앱은 네이티브 소켓이 대신 받는다.
  const loggedInToken = auth?.accessToken;
  useEffect(() => {
    if (loggedInToken && !hasNativeBridge()) getAppSocket();
  }, [loggedInToken]);

  const save = (data) => {
    authRef.current = data;
    setAuth(data);
    try {
      if (data) localStorage.setItem(STORAGE, JSON.stringify(data));
      else localStorage.removeItem(STORAGE);
    } catch {}
  };

  const clearForeignDraftsAfterLogin = () => {
    // 비회원 작성 화면에서 막 가입·로그인한 경우(30분 안)만 비회원 초안을 남긴다(util/guestDraft).
    //  회원 초안은 회원 번호별(muyeon.draft.u<id>:*)이라 다른 계정에는 보이지 않으므로 그대로 둔다.
    if (!consumePreserveDraftOnLogin()) clearGuestDrafts();
    clearJobBoardTabMemory();
  };

  const login = async (email, password) => {
    const res = await callLogin({ email, password });
    const data = { accessToken: res.data.accessToken, sessionId: res.data.sessionId, user: res.data.user };
    clearForeignDraftsAfterLogin();
    save(data);
    setLastLoginProvider(LOGIN_PROVIDER.EMAIL);
    syncNativeAfterAuth(data); // 네이티브 키체인 토큰 + FCM 등록(비동기)
    return data;
  };

  // 카카오 로그인: 콜백에서 받은 code 를 백엔드로 보내 토큰교환 → 자체 JWT 수신.
  // redirectUri 는 토큰 교환 검증용으로 authorize 때와 동일하게 보내야 함.
  const kakaoLogin = async (code) => {
    const res = await callKakaoLogin({ code, redirectUri: kakaoRedirectUri() });
    const data = { accessToken: res.data.accessToken, sessionId: res.data.sessionId, user: res.data.user };
    clearForeignDraftsAfterLogin();
    save(data);
    setLastLoginProvider(LOGIN_PROVIDER.KAKAO);
    syncNativeAfterAuth(data);
    return { ...data, isNew: !!res.data.isNew }; // isNew → 추가정보 입력 단계로
  };

  // ★ 2026-10-31 네이버 로그인: 콜백의 code+state 를 백엔드로 보내 토큰교환 → 자체 JWT 수신.
  const naverLogin = async (code, state) => {
    const res = await callNaverLogin({ code, state });
    const data = { accessToken: res.data.accessToken, sessionId: res.data.sessionId, user: res.data.user };
    clearForeignDraftsAfterLogin();
    save(data);
    setLastLoginProvider(LOGIN_PROVIDER.NAVER);
    syncNativeAfterAuth(data);
    return { ...data, isNew: !!res.data.isNew }; // isNew → 추가정보 입력 단계로
  };

  // 애플 로그인: 네이티브가 준 identityToken 을 백엔드로 보내 검증 → 자체 JWT 수신.
  // (애플 OAuth 는 웹뷰에서 차단되므로 네이티브 ASAuthorization 으로만 진입)
  const appleLogin = async (credential) => {
    const res = await callAppleLogin({
      identityToken: credential?.identityToken,
      name: credential?.name,
    });
    const data = { accessToken: res.data.accessToken, sessionId: res.data.sessionId, user: res.data.user };
    clearForeignDraftsAfterLogin();
    save(data);
    setLastLoginProvider(LOGIN_PROVIDER.APPLE);
    syncNativeAfterAuth(data);
    return { ...data, isNew: !!res.data.isNew }; // isNew → 추가정보 입력 단계로
  };

  const register = async (payload) => {
    const res = await callRegister(payload);
    const data = { accessToken: res.data.accessToken, sessionId: res.data.sessionId, user: res.data.user };
    save(data); // 가입 즉시 로그인
    syncNativeAfterAuth(data);
    return data;
  };

  const logout = () => {
    const leavingUserId = authRef.current?.user?.id;
    save(null);
    disconnectAppSocket();
    setActiveTypeState(null);
    try { localStorage.removeItem(ACTIVE_KEY); } catch {}
    // 초안 정리: 공용 기기에서 다음 사용자에게 남의 작성 내용이 붙지 않게.
    //  나가는 회원의 초안(muyeon.draft.u<id>:*) + 회원 번호 없는 초안(비회원·예전 방식).
    clearUserDrafts(leavingUserId);
    clearGuestDrafts();
    clearJobBoardTabMemory();
  };

  // 프로필 수정 후 저장된 유저 정보 갱신 (마이페이지 활동명 등 즉시 반영)
  const updateUser = (partial) => {
    const current = authRef.current;
    if (!current) return;
    save({ ...current, user: { ...current.user, ...partial } });
  };

  return (
    <AuthContext.Provider
      value={{
        auth,
        user: auth?.user ?? null,
        isLoggedIn: !!auth,
        activeType,           // 현재 활동 유형(보유유형 중 하나 or "GENERAL")
        setActiveType,        // 활동 유형 전환
        login,
        kakaoLogin,
        naverLogin,
        appleLogin,
        register,
        logout,
        updateUser,
        refreshRoles,         // 서버 최신 보유역할로 user.memberTypes 재동기화
        // 후니드 잔존 페이지 호환용 스텁
        member: auth?.user ?? null,
        device: null,
        isFirst: false,
        getMemberInfo: async () => auth?.user ?? null,
        getDeviceInfo: async () => null,
        setDeviceInfo: () => {},
        changeIsFirst: () => {},
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
